/** FAQ / INFO helpers for the optional Additional Information onboarding / assets-page section. */

export type AdditionalInfoMode = 'faq' | 'info';

export interface AdditionalInfoFaqRow {
  question: string;
  answer: string;
}

export interface AdditionalInfoFeedbackItem {
  type: 'FAQ' | 'INFO';
  data: Record<string, unknown>[];
}

/** Minimal stored-feedback shape used by GET mapping (FAQ / INFO). */
export interface AdditionalInfoStoredItem {
  type?: string;
  data?: Record<string, unknown> | Record<string, unknown>[] | null;
  createdAt?: string;
  assetId?: string;
  /** GET groups records as `{ assetId, feedback: [ { type, data } ] }`. */
  feedback?: AdditionalInfoStoredItem[] | null;
}

export const FAQ_ANSWER_MIN = 30;
export const FAQ_ANSWER_MAX = 200;
export const INFO_MAX = 1000;
export const INITIAL_FAQ_ROWS = 1;

export const ADDITIONAL_INFO_BOTH_FILLED_MESSAGE =
  'Additional information can include FAQ or information, not both. Clear one section and upload again.';

export const FAQ_INCOMPLETE_MESSAGE =
  "You can't proceed without answering the question. If you don't have answer right now please remove the question and proceed.";

/** Why this FAQ row cannot be saved or followed by another one. Empty when the row is blank or complete. */
export function faqRowErrorMessage(row: AdditionalInfoFaqRow): string {
  const question = row.question.trim();
  const answer = row.answer.trim();
  if (!question && !answer) {
    return '';
  }
  if (!question) {
    return 'Enter a question for this answer.';
  }
  if (answer.length < FAQ_ANSWER_MIN) {
    return `Answer must be at least ${FAQ_ANSWER_MIN} characters. Finish this FAQ before adding another.`;
  }
  if (answer.length > FAQ_ANSWER_MAX) {
    return `Answer must be at most ${FAQ_ANSWER_MAX} characters.`;
  }
  return '';
}

export function additionalInfoSectionHasContent(
  mode: AdditionalInfoMode,
  faqRows: AdditionalInfoFaqRow[],
  infoText: string,
): boolean {
  return mode === 'faq' ? faqRows.some(isFaqRowStarted) : infoText.trim().length > 0;
}

/** Shown before replacing the section the user already filled. */
export function additionalInfoReplaceMessage(next: AdditionalInfoMode): string {
  return next === 'info'
    ? 'The FAQ section will be removed. You can add Information only after that.'
    : 'The Information section will be removed. You can add FAQ only after that.';
}

export function createEmptyFaqRow(): AdditionalInfoFaqRow {
  return { question: '', answer: '' };
}

export function createInitialFaqRows(): AdditionalInfoFaqRow[] {
  return Array.from({ length: INITIAL_FAQ_ROWS }, () => createEmptyFaqRow());
}

export function isFaqRowStarted(row: AdditionalInfoFaqRow): boolean {
  return row.question.trim().length > 0 || row.answer.trim().length > 0;
}

export function isFaqRowComplete(row: AdditionalInfoFaqRow): boolean {
  const question = row.question.trim();
  const answer = row.answer.trim();
  return (
    question.length > 0 &&
    answer.length >= FAQ_ANSWER_MIN &&
    answer.length <= FAQ_ANSWER_MAX
  );
}

export function faqRowsAreValid(rows: AdditionalInfoFaqRow[]): boolean {
  return rows.every((row) => !isFaqRowStarted(row) || isFaqRowComplete(row));
}

/** Add More stays off until every FAQ has a question and an answer of at least 30 characters. */
export function canAddFaqRow(rows: AdditionalInfoFaqRow[]): boolean {
  return rows.length > 0 && rows.every(isFaqRowComplete);
}

function faqRowPayload(row: AdditionalInfoFaqRow): Record<string, unknown> {
  return {
    question: row.question.trim(),
    answer: row.answer.trim(),
  };
}

export function collectFeedbackItems(
  mode: AdditionalInfoMode | null,
  rows: AdditionalInfoFaqRow[],
  infoText: string,
): AdditionalInfoFeedbackItem[] {
  if (mode === 'faq') {
    const data = rows.filter(isFaqRowComplete).map(faqRowPayload);
    return data.length > 0 ? [{ type: 'FAQ', data }] : [];
  }
  if (mode === 'info') {
    const information = infoText.trim();
    if (!information) {
      return [];
    }
    return [{ type: 'INFO', data: [{ information }] }];
  }
  return [];
}

function asTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim();
}

/** GET `data` may be one entry or the full list for that type. */
function feedbackDataEntries(
  data: AdditionalInfoStoredItem['data'],
): Record<string, unknown>[] {
  if (Array.isArray(data)) {
    return data.filter(
      (entry): entry is Record<string, unknown> =>
        !!entry && typeof entry === 'object' && !Array.isArray(entry),
    );
  }
  if (data && typeof data === 'object') {
    return [data];
  }
  return [];
}

function faqRowTextEquals(a: AdditionalInfoFaqRow, b: AdditionalInfoFaqRow): boolean {
  return a.question.trim() === b.question.trim() && a.answer.trim() === b.answer.trim();
}

/** Unwrap GET `{ assetId, feedback: [...] }` rows into per-type records. */
function flattenStoredFeedbackItems(
  items: AdditionalInfoStoredItem[] | null | undefined,
): AdditionalInfoStoredItem[] {
  const out: AdditionalInfoStoredItem[] = [];
  for (const item of items ?? []) {
    const nested = item.feedback;
    if (Array.isArray(nested) && nested.length > 0) {
      for (const entry of nested) {
        if (entry && typeof entry === 'object') {
          out.push(entry);
        }
      }
      continue;
    }
    out.push(item);
  }
  return out;
}

/**
 * Map stored provider-feedback records onto the FAQ rows + latest INFO text
 * used by the Additional Information form.
 */
export function splitFeedbackItems(items: AdditionalInfoStoredItem[]): {
  faqRows: AdditionalInfoFaqRow[];
  infoText: string;
} {
  const faqRows: AdditionalInfoFaqRow[] = [];
  let latestInfo: { createdAt: string; information: string } | null = null;

  for (const item of flattenStoredFeedbackItems(items)) {
    if (item.type === 'FAQ') {
      for (const entry of feedbackDataEntries(item.data)) {
        const question = asTrimmedString(entry['question']);
        const answer = asTrimmedString(entry['answer']);
        if (question || answer) {
          faqRows.push({ question, answer });
        }
      }
      continue;
    }
    if (item.type !== 'INFO') {
      continue;
    }
    let information = '';
    for (const entry of feedbackDataEntries(item.data)) {
      const text =
        asTrimmedString(entry['information']) || asTrimmedString(entry['comment']);
      if (text) {
        information = text;
      }
    }
    if (!information) {
      continue;
    }
    const createdAt = item.createdAt ?? '';
    if (!latestInfo || createdAt > latestInfo.createdAt) {
      latestInfo = { createdAt, information };
    }
  }

  return { faqRows, infoText: latestInfo?.information ?? '' };
}

/** True only when a FAQ/INFO record has actual content — an emptied-out record (deleted) doesn't count. */
export function hasFaqOrInfoFeedback(items: AdditionalInfoStoredItem[] | null | undefined): boolean {
  return flattenStoredFeedbackItems(items).some((item) => {
    if (item.type === 'FAQ') {
      return feedbackDataEntries(item.data).some(
        (entry) => asTrimmedString(entry['question']) || asTrimmedString(entry['answer']),
      );
    }
    if (item.type === 'INFO') {
      return feedbackDataEntries(item.data).some(
        (entry) => asTrimmedString(entry['information']) || asTrimmedString(entry['comment']),
      );
    }
    return false;
  });
}

/**
 * Collect FAQ/INFO payloads that changed relative to what GET returned.
 * PUT replaces the full list for that type, so a FAQ change sends every complete row —
 * including an empty list when the user deleted every existing FAQ/INFO entry.
 */
export function collectChangedFeedbackItems(
  faqRows: AdditionalInfoFaqRow[],
  initialFaqRows: AdditionalInfoFaqRow[],
  infoText: string,
  initialInfoText: string,
): AdditionalInfoFeedbackItem[] {
  const items: AdditionalInfoFeedbackItem[] = [];
  const completeFaqs = faqRows.filter(isFaqRowComplete);
  const faqChanged =
    completeFaqs.length !== initialFaqRows.length ||
    completeFaqs.some((row, index) => {
      const initial = initialFaqRows[index];
      return !initial || !faqRowTextEquals(row, initial);
    });
  if (faqChanged) {
    items.push({ type: 'FAQ', data: completeFaqs.map(faqRowPayload) });
  }

  const information = infoText.trim();
  if (information !== initialInfoText.trim()) {
    items.push({ type: 'INFO', data: information ? [{ information }] : [] });
  }

  return items;
}

/**
 * Assets modify modal shows one section. FAQ wins when a question was already
 * saved (including older rows that also have information).
 */
export function additionalInfoEditMode(
  faqRows: AdditionalInfoFaqRow[],
  infoText: string,
): AdditionalInfoMode {
  return faqRows.some(isFaqRowStarted) || !infoText.trim() ? 'faq' : 'info';
}

/**
 * Changes for the section shown in the modify modal. A stored value of the
 * other type is cleared on that same save, so an asset cannot keep both.
 */
export function collectEditModeFeedbackItems(
  mode: AdditionalInfoMode,
  faqRows: AdditionalInfoFaqRow[],
  initialFaqRows: AdditionalInfoFaqRow[],
  infoText: string,
  initialInfoText: string,
): AdditionalInfoFeedbackItem[] {
  if (mode === 'faq') {
    const items = collectChangedFeedbackItems(
      faqRows,
      initialFaqRows,
      initialInfoText,
      initialInfoText,
    );
    if (
      initialInfoText.trim() &&
      (items.length > 0 || !infoText.trim())
    ) {
      items.push({ type: 'INFO', data: [] });
    }
    return items;
  }
  const items = collectChangedFeedbackItems(
    initialFaqRows,
    initialFaqRows,
    infoText,
    initialInfoText,
  );
  if (
    initialFaqRows.some(isFaqRowStarted) &&
    (items.length > 0 || !faqRows.some(isFaqRowStarted))
  ) {
    items.push({ type: 'FAQ', data: [] });
  }
  return items;
}

/**
 * True once the edit form differs from what GET loaded — enables the Modify modal's
 * save button. Counts any started row (not just complete ones) so typing shows as dirty
 * before the 30-char answer minimum is met.
 */
export function hasEditedAdditionalInfo(
  faqRows: AdditionalInfoFaqRow[],
  initialFaqRows: AdditionalInfoFaqRow[],
  infoText: string,
  initialInfoText: string,
): boolean {
  const startedRows = faqRows.filter(isFaqRowStarted);
  const faqChanged =
    startedRows.length !== initialFaqRows.length ||
    startedRows.some((row, index) => !faqRowTextEquals(row, initialFaqRows[index] ?? createEmptyFaqRow()));
  return faqChanged || infoText.trim() !== initialInfoText.trim();
}

export interface TemplateAdditionalInfoState {
  mode: AdditionalInfoMode | null;
  faqRows: AdditionalInfoFaqRow[];
  infoText: string;
}

/** Empty optional additionalInfo block for JSON metadata templates. */
export function emptyTemplateAdditionalInfo(): {
  faq: AdditionalInfoFaqRow[];
  information: string;
} {
  return { faq: [createEmptyFaqRow()], information: '' };
}

function readTemplateAdditionalInfo(json: unknown): {
  faq: AdditionalInfoFaqRow[];
  information: string;
} {
  if (!json || typeof json !== 'object') {
    return { faq: [], information: '' };
  }
  const raw = (json as Record<string, unknown>)['additionalInfo'];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { faq: [], information: '' };
  }
  const obj = raw as Record<string, unknown>;
  const information = asTrimmedString(obj['information']).slice(0, INFO_MAX);
  const faq: AdditionalInfoFaqRow[] = [];
  if (Array.isArray(obj['faq'])) {
    for (const item of obj['faq']) {
      if (!item || typeof item !== 'object') {
        continue;
      }
      const rec = item as Record<string, unknown>;
      const question = asTrimmedString(rec['question']);
      const answer = asTrimmedString(rec['answer']).slice(0, FAQ_ANSWER_MAX);
      if (question || answer) {
        faq.push({ question, answer });
      }
    }
  }
  return { faq, information };
}

/** Upload-time error when the optional template fills both FAQ and information. */
export function additionalInfoMutualExclusionError(json: unknown): string | null {
  const { faq, information } = readTemplateAdditionalInfo(json);
  if (faq.some(isFaqRowStarted) && information) {
    return ADDITIONAL_INFO_BOTH_FILLED_MESSAGE;
  }
  return null;
}

/** Map template additionalInfo onto the onboarding form (FAQ xor INFO xor empty). */
export function parseTemplateAdditionalInfo(json: unknown): TemplateAdditionalInfoState {
  const { faq, information } = readTemplateAdditionalInfo(json);
  if (faq.some(isFaqRowStarted)) {
    return { mode: 'faq', faqRows: faq, infoText: '' };
  }
  if (information) {
    return { mode: 'info', faqRows: createInitialFaqRows(), infoText: information };
  }
  return { mode: null, faqRows: createInitialFaqRows(), infoText: '' };
}
